import React, { useState, useEffect } from 'react';
import TableRow from '../../../Components/subcomponents/TableRow';
import ResultModal from '../../../Components/Modals/ResultModal';
import TextModal from '../../../Components/Modals/TextModal';
import { FaSyncAlt } from 'react-icons/fa';
import { MdOutlineMessage } from "react-icons/md";
import currentDomain from '../../../Utils/Utils';
import { IoTrophyOutline } from "react-icons/io5";
import WinnersModal from '../../../Components/Modals/WinnersModal';
import { apiClient } from '../../../services/api';


function ViewResults() {
  const [searchQuery, setSearchQuery] = useState('');
  const [entries, setEntries] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [selectedEntry, setSelectedEntry] = useState(null); // State to track selected entry for modal
  const [isModalOpen, setIsModalOpen] = useState(false); // State to control modal visibility
  const [isWinnersModalOpen, setIsWinnersModalOpen] = useState(false); // State to control winners modal visibilit
  const [selectedClass, setSelectedClass] = useState(''); // State to track selected class
  const [classes, setClasses] = useState([]); // State to store unique classes
  const [selectedEntries, setSelectedEntries] = useState([]); // State to track selected entries
  const [isTextModalOpen, setIsTextModalOpen] = useState(false); // State to control text modal visibility
  const [parkingZones, setParkingZones] = useState([]); // State to store parking zones
  const [specialAwards, setSpecialAwards] = useState([]);
 
  // Fetch entries from the backend
  const fetchEntries = async () => {
    setIsLoading(true);
    try {
      const results = await fetch(`${currentDomain(location)}/api/results`);
      const data = await results.json();
      setEntries(data);

      setSpecialAwards(await apiClient.getSpecialAwards());
      
      const classes = await fetch(`${currentDomain(location)}/api/class?exclusive=false`);
      const classData = await classes.json();
      // Extract unique classes
      const uniqueClasses = [...new Set(classData.map(entry => entry.name))];
      setClasses(uniqueClasses);

      // Fetch parking zones
      const parkingResponse = await fetch(`${currentDomain(location)}/api/parking`);
      const parkingData = await parkingResponse.json();
      setParkingZones(parkingData);
    } catch (error) {
      toast.error('Failed to fetch results');
    } finally {
      setIsLoading(false);
    }
  };

  // Returns { [className]: { first: entry|null, second: entry|null } }
  

  // Helper function to get parking zone info by ID
  const getParkingZoneInfo = (zoneId) => {
    if (!zoneId || !parkingZones.length) return { name: 'Not Assigned', hexcolor: null };
    const zone = parkingZones.find(zone => zone.order === zoneId);
    return zone ? { name: zone.name, hexcolor: zone.hexcolor } : { name: 'Unknown Zone', hexcolor: null };
  };


  // Initial fetch on component mount
  useEffect(() => {
    fetchEntries();
  }, []);

  // Handle refresh
  const handleRefresh = () => {
    fetchEntries(); // This will re-fetch the entries
    setSelectedEntries([]); // Reset selected entries on refresh
  };

  // Function to open the modal and pass the selected entry data
  const handleEdit = (entry) => {
    setSelectedEntry(entry);
    setIsModalOpen(true);
  };

  const handleSelect = ([entry]) => {
    selectedEntries.includes(entry)
      ? setSelectedEntries(selectedEntries.filter(e => e !== entry))
      : setSelectedEntries([...selectedEntries, entry]);
  };

  // Function to handle select all/deselect all for currently displayed entries
  const handleSelectAll = () => {
    const allDisplayedSelected = filteredEntries.every(entry => 
      selectedEntries.includes(entry)
    );
    
    if (allDisplayedSelected) {
      // Deselect all displayed entries
      setSelectedEntries(selectedEntries.filter(entry => 
        !filteredEntries.includes(entry)
      ));
    } else {
      // Select all displayed entries
      const newSelectedEntries = [...selectedEntries];
      filteredEntries.forEach(entry => {
        if (!newSelectedEntries.includes(entry)) {
          newSelectedEntries.push(entry);
        }
      });
      setSelectedEntries(newSelectedEntries);
    }
  };
  
  const handleMsgButton = async () => {
    setIsTextModalOpen(true);
  };

  // Function to close the modal
  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedEntry(null);
    fetchEntries();
  };

  const handleOpenWinnersModal = () => {
    setIsWinnersModalOpen(true);
  };

  const handleCloseWinnersModal = () => {
    setIsWinnersModalOpen(false);
    setSelectedEntry(null);
    fetchEntries();
  }

  // Filter entries based on selected class and search query
  const filteredEntries = entries
    .filter((entry) => {
      const query = searchQuery.toLowerCase();
      const fullName = `${entry.owner_first_name} ${entry.owner_last_name}`.toLowerCase();
      const isPaidString = entry.ispaid ? "yes" : "no"; // Convert boolean to "Yes" or "No"
      const parkingZoneInfo = getParkingZoneInfo(entry.parking_zone);
      const parkingZoneName = parkingZoneInfo.name.toLowerCase();

      return (
        (selectedClass === '' || entry.class_name === selectedClass) && // Filter by class
        (
          fullName.includes(query) ||
          entry.make.toLowerCase().includes(query) ||
          entry.model.toLowerCase().includes(query) ||
          entry.year.toString().includes(query) ||
          entry.car_number.toString().includes(query) ||
          entry.owner_phone.includes(query) ||
          isPaidString.includes(query) || // Include isPaid ("Yes" or "No") in the search
          parkingZoneName.includes(query) // Include parking zone name in the search
        )
      );
    })
    .sort((a, b) => {
      const query = searchQuery.toLowerCase();
      const aMatch = a.owner_first_name.toLowerCase().includes(query) || a.make.toLowerCase().includes(query);
      const bMatch = b.owner_first_name.toLowerCase().includes(query) || b.make.toLowerCase().includes(query);
      return aMatch === bMatch ? 0 : aMatch ? -1 : 1;
    });

  return (
    <div>
      <div className='text-center'>
        <h1 className="font-bold text-2xl py-8">Results</h1>
      </div>
      <div className="flex justify-between items-center pb-4 gap-20">
        {/* Class selector dropdown */}
        <select
          value={selectedClass}
          onChange={e => setSelectedClass(e.target.value)}
          className="border p-2 w-1/3"
        >
          <option value="">Select Class</option>
          {classes.map((className, index) => (
            <option key={index} value={className}>
              {className}
            </option>
          ))}
        </select>

        <input
          type="text"
          placeholder="Search by year, make, model, owner, etc."
          className="border p-2 w-2/3"
          value={searchQuery}
          onChange={e => setSearchQuery(e.target.value)}
        />
        <div className='flex gap-8'>
          <div className='w-10 h-10 flex items-center justify-center rounded-full outline outline-1 outline-gray-500 hover:bg-gray-100'>
            <button onClick={handleMsgButton} className='flex items-center justify-center w-full h-full'>
              <MdOutlineMessage size={25} />
            </button>
          </div>
          <div className='w-10 h-10 flex items-center justify-center rounded-full outline outline-1 outline-gray-500 hover:bg-gray-100'>
            <button onClick={handleOpenWinnersModal} className='outline-2 outline-gray-500 rounded-full p-2 hover:bg-gray-100'>
              <IoTrophyOutline size={20} />
            </button>
          </div>
          <div className='w-10 h-10 flex items-center justify-center rounded-full outline outline-1 outline-gray-500 hover:bg-gray-100'>
            <button onClick={handleRefresh} className='outline-2 outline-gray-500 rounded-full p-2 hover:bg-gray-100'>
              <FaSyncAlt size={20} />
            </button>
          </div>
        </div>

      </div>
      {isLoading ? <p>Loading...</p> : (
        <div className="overflow-auto pr-4">
          <table className="w-full border border-black">
            <thead>
              <tr className="bg-gray-200">
                <th className="text-l cursor-pointer hover:bg-gray-300 transition-colors" onClick={handleSelectAll}>
                  Select
                </th>
                <th className="text-l">Place</th>
                <th className="text-l">#</th>
                <th className="text-l">Year</th>
                <th className="text-l">Make Model</th>
                <th className="text-l">Class</th>
                <th className="text-l">Owner</th>
                <th className="text-l">Avg Score</th>
                <th className="text-l">Ballots</th>
                <th className="text-l">Parked</th>
                <th className="text-l">Phone</th>
                <th className="text-l"></th>
              </tr>
            </thead>
            <tbody className='text-sm'>
              {filteredEntries.length > 0 ? (
                filteredEntries.map((entry, index) => {
                  const parkingZoneInfo = getParkingZoneInfo(entry.parking_zone);
                  return (
                    <TableRow
                      key={index}
                      year={entry.year}
                      make={entry.make}
                      model={entry.model}
                      className={entry.class_name}
                      owner={`${entry.owner_first_name} ${entry.owner_last_name}`}
                      isPaid={entry.avg_score ? entry.avg_score : "-"}
                      phone={entry.owner_phone}
                      ballots={entry.ballots ? entry.ballots : "-"}
                      num={entry.car_number}
                      onEdit={() => handleEdit(entry)} // Open modal with entry details on Edit
                      place={entry.avg_score ? index + 1 : "-"} // Display entry number only if avg_score is present 16-1 13-2 20-0
                      toggleCheckbox={() => handleSelect([entry])} // Handle selection
                      isSelected={selectedEntries.includes(entry)} // Pass selection state
                      parkingZone={
                        <div className="flex items-center gap-2">
                          {parkingZoneInfo.hexcolor && (
                            <div 
                              className="w-4 h-4 border border-gray-300 flex-shrink-0"
                              style={{ backgroundColor: `#${parkingZoneInfo.hexcolor}` }}
                            />
                          )}
                          <span>{parkingZoneInfo.name}</span>
                        </div>
                      }
                    />
                  );
                })
              ) : (
                <tr>
                  <td colSpan="12" className="text-center py-4">No matching entries found.</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}
      <WinnersModal isOpen={isWinnersModalOpen} entries={entries} SpecialCategories={specialAwards} onClose={handleCloseWinnersModal}/>
      <ResultModal isOpen={isModalOpen} onClose={handleCloseModal} entry={selectedEntry} />
      <TextModal isOpen={isTextModalOpen} onClose={() => setIsTextModalOpen(false)} selected={selectedEntries} />
    </div>
  );
}

export default ViewResults;

{/*
  Best in Show
  VFW Choice
  Mayor's Choice
  Chairmans Choice
  BLEF Choice
  Best Paint
  Best Interior
  Best Engine
  Best Stock Unrestored
  Best Stock Restored
  Best Chevy
  Best Ford
  Best Mopar
  Best BOP
  */}